// frontend/src/pages/ClaseRecursosPage.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import { FiPlus } from 'react-icons/fi';
import { claseRecursoService } from '../services/onebaseService';
import CrearEditarClaseRecursoModal from '../components/CrearEditarClaseRecursoModal';
import ClaseRecursosTree from '../components/ClaseRecursosTree';
import Loading from '../components/Loading';
import './ClaseRecursosPage.css';

/** Construye árbol jerárquico desde lista plana usando campo `parent` */
const buildTree = (flatList) => {
  const map = {};
  flatList.forEach(item => { map[item.id] = { ...item, children: [] }; });
  const roots = [];
  flatList.forEach(item => {
    if (item.parent) {
      if (map[item.parent]) map[item.parent].children.push(map[item.id]);
      else roots.push(map[item.id]);
    } else {
      roots.push(map[item.id]);
    }
  });
  return roots;
};

/** Filtrado recursivo: devuelve árbol con solo nodos que hacen match (o tienen hijos que hacen match) */
const filterTree = (nodes, texto) => {
  if (!texto) return nodes;
  const t = texto.toLowerCase();
  return nodes.reduce((acc, node) => {
    const hijos = filterTree(node.children || [], texto);
    const match = node.nombre.toLowerCase().includes(t) || (node.codigo && node.codigo.toLowerCase().includes(t));
    if (match || hijos.length > 0) acc.push({ ...node, children: hijos });
    return acc;
  }, []);
};

const ClaseRecursosPage = () => {
  const [clases, setClases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [claseEditando, setClaseEditando] = useState(null);
  const [padreInicial, setPadreInicial] = useState(null);
  const [filtroNombre, setFiltroNombre] = useState('');

  useEffect(() => { cargarClases(); }, []);

  const cargarClases = async () => {
    try {
      setLoading(true);
      const response = await claseRecursoService.getAll({ status: 'activo' });
      setClases(response.data?.results || response.data || []);
    } catch (error) {
      console.error('Error al cargar clases de recursos:', error);
      toast.error('Error al cargar las clases de recursos');
    } finally {
      setLoading(false);
    }
  };

  const arbol = useMemo(() => buildTree(clases), [clases]);
  const arbolFiltrado = useMemo(() => filterTree(arbol, filtroNombre), [arbol, filtroNombre]);

  const handleCrear = () => {
    setClaseEditando(null);
    setPadreInicial(null);
    setShowModal(true);
  };

  const handleCrearHijo = (padre) => {
    setClaseEditando(null);
    setPadreInicial(padre);
    setShowModal(true);
  };

  const handleEditar = (clase) => {
    setClaseEditando(clase);
    setPadreInicial(null);
    setShowModal(true);
  };

  const handleEliminar = async (clase) => {
    if (!window.confirm(`¿Está seguro de eliminar la clase "${clase.nombre}"?\n\nEsto también eliminará todas sus subclases.`)) return;
    try {
      await claseRecursoService.delete(clase.id);
      toast.success('Clase de recurso eliminada correctamente');
      cargarClases();
    } catch (error) {
      console.error('Error al eliminar clase:', error);
      toast.error('Error al eliminar la clase de recurso');
    }
  };

  const handleCerrarModal = () => {
    setShowModal(false);
    setClaseEditando(null);
    setPadreInicial(null);
  };

  const handleSuccess = () => {
    cargarClases();
    handleCerrarModal();
  };

  if (loading) return <Loading />;

  return (
    <div className="clase-recursos-page">
      <div className="page-header">
        <h1>🗂️ Clases de Recursos</h1>
        <button className="btn-primary" onClick={handleCrear}>
          <FiPlus /> Nueva Clase
        </button>
      </div>

      <div className="info-section">
        <div className="info-card">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>Estructura jerárquica de 3 niveles:</strong>
            <ul>
              <li><strong>N1:</strong> Clase principal (ej: Materiales)</li>
              <li><strong>N2:</strong> Subclase (ej: Construcción)</li>
              <li><strong>N3:</strong> Clase imputable con cuenta asociada (ej: Cemento)</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="filtros-section">
        <div className="filtro-item">
          <label htmlFor="filtro-nombre">Buscar</label>
          <input
            id="filtro-nombre"
            type="text"
            placeholder="Buscar por nombre o código..."
            value={filtroNombre}
            onChange={(e) => setFiltroNombre(e.target.value)}
            className="filtro-input"
          />
        </div>
      </div>

      <div className="clases-tree-container">
        {arbolFiltrado.length === 0 ? (
          <div className="empty-row">
            {clases.length === 0
              ? 'No hay clases de recursos registradas'
              : 'No hay clases que coincidan con el filtro'}
          </div>
        ) : (
          <ClaseRecursosTree
            tree={arbolFiltrado}
            onCrearHijo={handleCrearHijo}
            onEditar={handleEditar}
            onEliminar={handleEliminar}
          />
        )}
      </div>

      {showModal && (
        <CrearEditarClaseRecursoModal
          clase={claseEditando}
          padreInicial={padreInicial}
          onClose={handleCerrarModal}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default ClaseRecursosPage;
